<script setup lang="ts">
/**
 * Dynamic table that renders each cell with a configurable Vue component.
 * Column components should render a <td> (e.g. MoneyCell, NameCell, TextCell).
 *
 * Callers must use markRaw() when building the columns array (this file does not
 * call markRaw). Example: component: markRaw(MoneyCell). Vue makes objects used
 * in the template reactive by default; a component definition is static and does
 * not need to be reactive. Without markRaw, the component gets wrapped in the
 * reactivity system (unnecessary overhead and can cause warnings or subtle issues).
 *
 * What markRaw() affects: only the component definition (the object in columns, e.g. MoneyCell).
 * It tells Vue not to make that definition reactive. It does not affect: the props passed to each
 * cell (e.g. amount, name) — those still come from the parent and are reactive; or the component
 * instances — they remain normal Vue components that re-render when their props or other reactive
 * deps change.
 *
 * Optional infinite scroll: pass onLoadMore (and hasNextPage, isFetchingNextPage). The table
 * renders a sentinel after the rows and uses IntersectionObserver to call onLoadMore when the
 * sentinel enters the viewport.
 */
import type { Component } from 'vue'
import { onUnmounted, ref, watch } from 'vue'

export interface Column<T = unknown> {
  // Column definition for DynamicTable. T = row type; props(row) is (row: T). Default T is unknown.

  key: string
  // Unique column id; used as key and for default cell value row[key].

  label: string
  // Header text shown in the table thead.

  component: Component
  // Vue component that renders the cell (must render a <td>). Use markRaw(Component).

  props?: (row: T) => Record<string, unknown>
  // Map row data to props for the cell component. If omitted, defaults to { value: row[col.key] }.

  maxWidth?: string
  // Max width of the column (CSS value, e.g. "12rem", "200px"). Passed to header and cell components.

  width?: string
  // Fixed/target width of the column (CSS value, e.g. "2.5rem"). Applied to <th> and forwarded to the
  // cell component as a `width` prop so the column doesn't get extra space distributed by table-layout: auto.

  align?: 'left' | 'center' | 'right'
  // Header text-align override (default: left). Set to 'right' for numeric/index columns.
}

/**
 * Table data can come from any array, e.g. TanStack Query (useQuery). Install with:
 *   npm i @tanstack/vue-query
 * See project README Installation section.
 */
const props = withDefaults(
  defineProps<{
    columns: Column[]
    data: unknown[]
    /** When set, enables infinite scroll: sentinel is shown and onLoadMore is called when it enters view. Typically pass fetchNextPage returned by TanStack Query useInfiniteQuery. */
    onLoadMore?: () => void
    /** Pass true when more pages exist (e.g. from useInfiniteQuery hasNextPage). */
    hasNextPage?: boolean
    /** Pass true while the next page is fetching (e.g. useInfiniteQuery isFetchingNextPage). */
    isFetchingNextPage?: boolean
    /** rootMargin for IntersectionObserver (e.g. "200px" to trigger 200px before sentinel is visible). */
    infiniteScrollRootMargin?: string
    /**
     * Optional per-row class hook. Receives the row and its index, returns a class
     * name (or list) merged onto the `<tr>` alongside the default `dynamic-table__tr`.
     * Use this to visually tag rows by state (e.g. active vs finished) without
     * coupling cell components to that state.
     */
    rowClass?: (row: unknown, index: number) => string | string[] | undefined
    /**
     * Optional click handler on the body `<tr>`. When set, rows render with
     * `cursor: pointer` and clicking anywhere in the row fires this callback.
     * Used by `ClientsTab` to open the client info card in the left flank
     * panel; combine with `rowKey` + `selectedRowKey` to highlight the
     * currently selected row. The handler is not fired by `rowOverride` rows
     * (event rows usually carry their own affordances and are not clickable).
     */
    onRowClick?: (row: unknown, index: number) => void
    /**
     * Stable identity for each row. When provided alongside `selectedRowKey`,
     * the table compares the two to decide which row gets the
     * `dynamic-table__tr--selected` highlight. Should be a primitive that is
     * stable across re-renders (e.g. the API id of the underlying record);
     * `index` is supplied as a fallback for callers that have no other id.
     */
    rowKey?: (row: unknown, index: number) => string | number
    /**
     * Currently selected row key. Compared against `rowKey(row)` to decide
     * which row receives the selected highlight. `null` (the default) leaves
     * every row in its idle state. Hover background still applies on top of
     * the selected highlight thanks to the `:hover` rule scoped to the
     * selected class.
     */
    selectedRowKey?: string | number | null
    /**
     * Optional per-row "override" component. When the callback returns a
     * Vue component for a given row, the table renders a single
     * `<tr><component :row :index :colspan /></tr>` instead of the normal
     * column cells; the override component is responsible for rendering
     * its own `<td>` (typically with `colspan` matching the number of
     * columns). Used for full-row event rows in the client card visits
     * table (disease ranges, monthly balance markers). Returning `null`
     * keeps the normal cell rendering. Use `markRaw` on the returned
     * component to avoid Vue wrapping it in reactivity.
     */
    rowOverride?: (row: unknown, index: number) => Component | null
    /**
     * Render a small pin toggle button in the upper-left corner of the header
     * (inside the first `<th>`). Clicking it makes the entire header row
     * `position: sticky` against the nearest scrolling ancestor so it stays
     * visible while the body scrolls. Defaults to `true` so every table is
     * pinnable; pass `:pinnable-header="false"` to opt out for a specific
     * table.
     */
    pinnableHeader?: boolean
    /**
     * Stable identifier used to persist the pinned state in `localStorage`
     * across sessions (one entry per table under
     * `plyas:table:<storageKey>:pinned`). Must be unique per table instance
     * in the app; typically matches the tab name (e.g. `services`, `clients`).
     * When omitted, the pinned state still works but is not remembered.
     */
    storageKey?: string
    /**
     * Callback for the compact add button rendered inside the last `<th>`.
     * When provided (together with `compactAddVisible: true`), a small `+`
     * circle button appears at the right edge of the last column header.
     * Mirrors the tab-level add button for use while the tab heading is
     * scrolled out of view.
     */
    onAdd?: () => void
    /**
     * Controls whether the compact `+` button is shown in the last header
     * cell. Typically driven by an IntersectionObserver on the tab heading:
     * set to `true` when the heading is off-screen. Defaults to `false` so
     * the button stays hidden until the caller opts in.
     */
    compactAddVisible?: boolean
    /**
     * Optional row edit handler. When set, a hidden actions column is appended
     * to the table. Hovering or focusing within a row reveals an edit icon
     * button aligned to the right. Clicking the button fires this callback and
     * stops propagation so `onRowClick` is not also triggered. Not fired for
     * `rowOverride` rows.
     */
    onRowEdit?: (row: unknown, index: number) => void
    /**
     * Optional row delete handler. When set, a trash icon button is shown in
     * the actions column alongside the edit button (if any). Clicking it fires
     * this callback and stops propagation. Not fired for `rowOverride` rows.
     */
    onRowDelete?: (row: unknown, index: number) => void
  }>(),
  {
    hasNextPage: false,
    isFetchingNextPage: false,
    infiniteScrollRootMargin: '200px',
    pinnableHeader: true,
    compactAddVisible: false,
  },
)

/**
 * Build the full `localStorage` key for this table's pinned-header flag, or
 * return null when the caller did not provide a `storageKey` (persistence
 * disabled).
 */
function pinnedStorageKey(): string | null {
  return props.storageKey ? `plyas:table:${props.storageKey}:pinned` : null
}

/**
 * Read the initial pinned state from `localStorage`. Defaults to `true` so
 * every table starts pinned for new visitors; only an explicit `"false"`
 * entry written by a prior unpin in this browser flips it off. Storage
 * unavailability (SSR, disabled, quota) also falls back to the pinned
 * default, since pinned is the better baseline UX.
 */
function readInitialPinned(): boolean {
  const key = pinnedStorageKey()
  if (!key || typeof window === 'undefined') return true
  try {
    return window.localStorage.getItem(key) !== 'false'
  } catch {
    return true
  }
}

const isHeaderPinned = ref(readInitialPinned())
// Reactive toggle for the sticky-header pin button. Initialized from
// `localStorage` (when `storageKey` is set) so the user's choice is restored
// across sessions. A watcher below writes changes back.

watch(isHeaderPinned, (value) => {
  const key = pinnedStorageKey()
  if (!key || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, value ? 'true' : 'false')
  } catch {
    // Storage may be disabled or full — fail silently and keep the in-memory state.
  }
})

const sentinelRef = ref<HTMLElement | null>(null)
/** IntersectionObserver is a built-in browser API; no import needed. */
let observer: IntersectionObserver | null = null

/** Watch sentinel ref and onLoadMore; when both set, observe sentinel and call onLoadMore on intersect. See docs/SYNTAX.md (Watchers) for watch() usage. */
watch(
  [sentinelRef, () => props.onLoadMore],
  ([el, onLoadMore]) => {
    observer?.disconnect()
    observer = null
    if (!el || !onLoadMore) return
    /** IntersectionObserver: calls the callback when the observed element enters or leaves the viewport (or the rootMargin zone). Used here to detect when the sentinel is near the viewport so we can load the next page. */
    observer = new IntersectionObserver(
      (entries) => {
        /** Array destructuring: take the first element of entries (IntersectionObserverEntry[]). We observe one element, so entry === entries[0]. */
        const [entry] = entries
        if (!entry?.isIntersecting || !props.hasNextPage || props.isFetchingNextPage) return
        onLoadMore()
      },
      { rootMargin: props.infiniteScrollRootMargin, threshold: 0 },
    )
    observer.observe(el)
  },
  /** flush: 'post' — run the watcher after Vue has updated the DOM, so sentinelRef is set when the sentinel element is in the tree. */
  { flush: 'post' },
)

onUnmounted(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <div class="dynamic-table-wrapper">
    <table
      class="dynamic-table font-tufte text-tufte-text"
      :class="{ 'dynamic-table--sticky-header': props.pinnableHeader && isHeaderPinned }"
    >
      <thead>
        <tr class="border-b border-tufte-border">
          <th
            v-for="(col, colIdx) in props.columns"
            :key="col.key"
            class="dynamic-table__th"
            :style="{
              ...(col.maxWidth ? { maxWidth: col.maxWidth, overflowWrap: 'break-word' } : {}),
              ...(col.width ? { width: col.width } : {}),
              ...(col.align ? { textAlign: col.align } : {}),
            }"
          >
            <!--
            First column gets the optional pin toggle. The `<span>` keeps the
            label inline-baseline aligned with the icon button so other column
            headers (which only render a label) still line up vertically.
          -->
            <template v-if="props.pinnableHeader && colIdx === 0">
              <span class="dynamic-table__th-pin-wrapper">
                <button
                  type="button"
                  class="dynamic-table__pin-btn"
                  :class="{ 'dynamic-table__pin-btn--active': isHeaderPinned }"
                  :aria-pressed="isHeaderPinned"
                  :aria-label="isHeaderPinned ? 'Unpin table header' : 'Pin table header to top'"
                  :title="isHeaderPinned ? 'Unpin header' : 'Pin header to top'"
                  @click="isHeaderPinned = !isHeaderPinned"
                >
                  <!--
                  Lucide-style pin icon (inline so we don't pull in an icon
                  package). `fill` toggles to currentColor when active so the
                  pinned state is visible at a glance even without color cues.
                -->
                  <svg
                    class="dynamic-table__pin-icon"
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    :fill="isHeaderPinned ? 'currentColor' : 'none'"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 17v5" />
                    <path
                      d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"
                    />
                  </svg>
                </button>
                <span class="dynamic-table__th-label">{{ col.label }}</span>
              </span>
            </template>
            <!--
            Last column hosts the compact add button when the tab heading has
            scrolled out of view (`compactAddVisible`) and a handler is wired
            (`onAdd`). The wrapper makes the label and button share the full
            cell width without disturbing the column's fixed pixel width.
          -->
            <template v-else-if="colIdx === props.columns.length - 1 && props.onAdd">
              <span class="dynamic-table__th-last-wrapper">
                {{ col.label }}
                <button
                  v-if="props.compactAddVisible"
                  type="button"
                  class="dynamic-table__compact-add-btn"
                  aria-label="Add"
                  @click="props.onAdd()"
                >
                  +
                </button>
              </span>
            </template>
            <template v-else>
              {{ col.label }}
            </template>
          </th>
          <th
            v-if="props.onRowEdit || props.onRowDelete"
            class="dynamic-table__th dynamic-table__th-actions"
            aria-label="Actions"
          />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in props.data"
          :key="index"
          class="dynamic-table__tr"
          :class="[
            props.rowClass ? props.rowClass(row, index) : undefined,
            props.onRowClick && !(props.rowOverride && props.rowOverride(row, index))
              ? 'dynamic-table__tr--clickable'
              : undefined,
            props.rowKey &&
            props.selectedRowKey != null &&
            props.rowKey(row, index) === props.selectedRowKey
              ? 'dynamic-table__tr--selected'
              : undefined,
            props.rowOverride && props.rowOverride(row, index)
              ? 'dynamic-table__tr--override'
              : undefined,
          ]"
          @click="
            props.onRowClick && !(props.rowOverride && props.rowOverride(row, index))
              ? props.onRowClick(row, index)
              : undefined
          "
        >
          <!--
          When `rowOverride` returns a component, render it once with
          `colspan` so it spans every column and skip the normal cell loop.
          The override component is responsible for its own `<td>` (so it
          can use `text-align`, `font-style`, etc. directly without going
          through DynamicTable's cell wrapper).
        -->
          <template v-if="props.rowOverride && props.rowOverride(row, index)">
            <component
              :is="props.rowOverride(row, index)!"
              :row="row"
              :index="index"
              :colspan="props.columns.length + (props.onRowEdit || props.onRowDelete ? 1 : 0)"
            />
          </template>
          <template v-else>
            <template v-for="col in props.columns" :key="col.key">
              <component
                :is="col.component"
                v-bind="{
                  /*
                   * Spread operator (...) copies each key of the right-hand object into this object.
                   * Example: column has key: 'name', props: (row) => ({ name: row.name }).
                   * For row = { name: 'Alice', balance: 100 }, col.props(row) returns { name: 'Alice' },
                   * so the cell receives { name: 'Alice' }. If the column had no props, we use
                   * { value: row[col.key] }, e.g. { value: 'Alice' } — so the cell gets a generic value prop.
                   */
                  ...(col.props
                    ? col.props(row)
                    : { value: (row as Record<string, unknown>)[col.key] }),
                  ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
                  ...(col.width ? { width: col.width } : {}),
                }"
              />
            </template>
            <td v-if="props.onRowEdit || props.onRowDelete" class="dynamic-table__td-actions">
              <div class="row-actions">
                <button
                  v-if="props.onRowEdit"
                  type="button"
                  class="icon-btn"
                  aria-label="Edit"
                  title="Edit"
                  @click.stop="props.onRowEdit(row, index)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14.5 4.5l5 5L8 21H3v-5L14.5 4.5z" />
                    <path d="M13 6l5 5" />
                  </svg>
                </button>
                <button
                  v-if="props.onRowDelete"
                  type="button"
                  class="icon-btn icon-btn--danger"
                  aria-label="Delete"
                  title="Delete"
                  @click.stop="props.onRowDelete(row, index)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 7h16" />
                    <path d="M9 7V4h6v3" />
                    <path d="M6 7l1 13h10l1-13" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                </button>
              </div>
            </td>
          </template>
        </tr>
      </tbody>
    </table>
    <div
      v-if="onLoadMore"
      ref="sentinelRef"
      class="dynamic-table-sentinel"
      role="status"
      aria-live="polite"
    >
      <span v-if="isFetchingNextPage">Loading more…</span>
      <span v-else-if="hasNextPage">&nbsp;</span>
    </div>
  </div>
</template>

<style scoped>
.dynamic-table {
  /*
   * `width: auto` (instead of `100%`) makes the table size to the sum of
   * its column widths — each column hugs its own content or its explicit
   * `width` prop — so the browser does not distribute leftover horizontal
   * space across auto-width columns. This eliminates the trailing blank
   * gap inside the last column when the middle layout column is wider
   * than the table actually needs (e.g. the Clients tab, where columns
   * have no explicit widths).
   */
  width: auto;
  border-collapse: collapse;
  /*
   * The page now has two flanking columns (`InfoViewLayout` /
   * `ConversationLayout`) around the main content area, so tables live inside
   * a narrower middle column than before. Dropping the default font from
   * `--text-tufte-base` (1.4rem) to `--text-tufte-sm` (1rem) — together with
   * the tighter cell padding below — lets the existing column widths fit
   * without horizontal scrolling while keeping the Tufte serif look.
   */
  font-size: var(--text-tufte-sm);
  line-height: var(--line-height-tufte-normal);
}

.dynamic-table__th {
  text-align: left;
  padding: 0.35rem 0.5rem;
  /* Semibold so column headers read clearly against body cells. */
  font-weight: 600;
  color: var(--color-tufte-text);
}

/* Body cells: same horizontal padding as headers so values line up under labels. */
.dynamic-table :deep(td) {
  box-sizing: border-box;
  padding: 0.35rem 0.5rem;
  vertical-align: middle;
}

.dynamic-table__tr {
  border-bottom: 1px solid var(--color-tufte-border);
}

/* Row hover: 20% theme border color on transparent so it works in light and dark. */
.dynamic-table__tr:hover {
  background: color-mix(in srgb, var(--color-tufte-border) 20%, transparent);
}

/*
 * Clickable rows (e.g. clients list with onRowClick) use a pointer cursor so
 * the user knows the entire row is interactive. The hover background above
 * still applies on top, providing the same affordance as before.
 */
.dynamic-table__tr--clickable {
  cursor: pointer;
}

/*
 * Selected row: a translucent tint of the foreground color so it works in
 * both Tufte light and dark themes without a hard-coded background. The
 * hover variant deepens the same tint so cursor feedback remains visible
 * over the selection.
 */
.dynamic-table__tr--selected {
  background: color-mix(in srgb, var(--color-tufte-text) 8%, transparent);
}

.dynamic-table__tr--selected:hover {
  background: color-mix(in srgb, var(--color-tufte-text) 14%, transparent);
}

/*
 * Override rows host their own `<td colspan>` event content (e.g.
 * `EventRowCell`). We disable hover and click cursor on these rows because
 * disease ranges and monthly balance markers are informational, not
 * actionable. The bottom border is kept so the row still acts as a visual
 * separator inside the merged ledger.
 */
.dynamic-table__tr--override {
  cursor: default;
}

.dynamic-table__tr--override:hover {
  background: transparent;
}

.dynamic-table-sentinel {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-muted);
}

/*
 * Pin toggle in the upper-left header cell. The wrapper is inline-flex so the
 * icon button and the column label share a baseline with neighboring header
 * cells (which render their label as plain text). Gap is intentionally small
 * to keep the `#` column compact.
 */
.dynamic-table__th-pin-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.dynamic-table__pin-btn {
  /* Reset the user-agent button chrome so the icon sits cleanly inside the th. */
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-tufte-muted);
  cursor: pointer;
  line-height: 0;
  border-radius: 2px;
}

.dynamic-table__pin-btn:hover,
.dynamic-table__pin-btn:focus-visible {
  color: var(--color-tufte-text);
  outline: none;
}

.dynamic-table__pin-btn:focus-visible {
  /* Keep keyboard focus visible without disturbing the table grid. */
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-tufte-text) 40%, transparent);
}

.dynamic-table__pin-btn--active {
  /* Active state uses the regular text color so the filled icon reads as "on". */
  color: var(--color-tufte-text);
}

.dynamic-table__pin-icon {
  /* Slight tilt mimics a pushed-in pin so the active state is recognizable. */
  transform: rotate(45deg);
  transition: transform 120ms ease-out;
}

.dynamic-table__pin-btn--active .dynamic-table__pin-icon {
  transform: rotate(0deg);
}

/*
 * Last-column wrapper: flex row so the column label stays left-aligned and
 * the compact add button is pushed to the right edge of the header cell.
 */
.dynamic-table__th-last-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
}

.dynamic-table__compact-add-btn {
  appearance: none;
  background: transparent;
  border: 1px solid var(--color-tufte-border);
  border-radius: 50%;
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  cursor: pointer;
  line-height: 1;
  padding: 0;
  flex-shrink: 0;
}

.dynamic-table__compact-add-btn:hover,
.dynamic-table__compact-add-btn:focus-visible {
  color: var(--color-tufte-text);
  border-color: var(--color-tufte-text);
  outline: none;
}

.dynamic-table__compact-add-btn:focus-visible {
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-tufte-text) 40%, transparent);
}

/*
 * Sticky header: when the user pins the header, every <th> sticks to the top
 * of the nearest scrolling ancestor (e.g. <main> in BaseLayout). We target
 * <th> directly because `position: sticky` on <thead>/<tr> is not supported in
 * all browsers when `border-collapse: collapse` is in effect. The opaque
 * background prevents body cells from showing through; the inset box-shadow
 * replaces the bottom border, which collapsed-border tables drop on sticky
 * cells.
 */
.dynamic-table--sticky-header thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--color-tufte-bg);
  box-shadow: inset 0 -1px 0 var(--color-tufte-border);
}

/* Actions column header: fixed width, no label. */
.dynamic-table__th-actions {
  width: 5rem;
}

/* Actions cell: same padding as body cells; content is right-aligned. */
.dynamic-table__td-actions {
  width: 5rem;
  padding: 0.35rem 0.5rem;
  vertical-align: middle;
}

/* Row action buttons: hidden by default, revealed on row hover or keyboard focus. */
.row-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.12s;
}

.dynamic-table__tr:hover .row-actions,
.dynamic-table__tr:focus-within .row-actions,
.dynamic-table__tr--selected .row-actions {
  opacity: 1;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: none;
  border: 1px solid transparent;
  color: var(--color-tufte-muted);
  cursor: pointer;
  border-radius: 2px;
  transition:
    background 0.1s,
    color 0.1s,
    border-color 0.1s;
  padding: 0;
}

.icon-btn:hover {
  background: color-mix(in srgb, var(--color-tufte-border) 40%, var(--color-tufte-bg));
  border-color: var(--color-tufte-border);
  color: var(--color-tufte-text);
}

.icon-btn--danger:hover {
  color: var(--color-money-negative);
  border-color: color-mix(in srgb, var(--color-money-negative) 35%, var(--color-tufte-border));
  background: color-mix(in srgb, var(--color-money-negative) 8%, var(--color-tufte-bg));
}

.icon-btn:focus-visible {
  outline: 1px solid var(--color-tufte-text);
  outline-offset: 1px;
}

.icon-btn svg {
  width: 1rem;
  height: 1rem;
  display: block;
}
</style>
